import { FC, PropsWithChildren, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { selectUserProjectTimeZone } from 'core/userContext';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { momentZodType } from 'lib/zod';
import {
  getOpenedNodeData,
  getIsJourneyStartTimeOptimizationEnabled,
} from 'modules/workflows/ducks';
import { MAX_OFFSET, RECUR_DAYS_RANGE } from './Scheduler.constants';
import {
  Day,
  EndRecurrenceType,
  RecurrenceScheduleType,
  SchedulerFormStateValues,
} from './Scheduler.types';
import { getDefaultFormStateValues } from './Scheduler.utils';

/**
 * This form provider is created using [React Hook Form](https://react-hook-form.com/)
 * and [zod](https://zod.dev/) for schema validation
 *
 * Note: while this form currently only encompasses Scheduler fields,
 * we may want to refactor this to be more generic as we migrate other
 * fields in this node editor to use React Hook Form components
 */

const schema = z
  .object({
    // Start date and time inputs:
    // nodeData.startDate
    startDate: momentZodType,
    // nodeData.minuteOfDay
    minuteOfDay: z.string(),
    // nodeData.timeZone
    timeZone: z.string().min(1).optional(),
    // nodeData.maxStartTimeOffsetInMinutes
    maxStartTimeOffsetInMinutes: z.number().min(0).max(MAX_OFFSET).optional(),

    // Recurrence inputs:
    // not currently supported by BE, will be converted to nodeData.recurDays
    recurrence: z.nativeEnum(RecurrenceScheduleType),
    // nodeData.recurDays
    recurDays: z.coerce
      .number()
      .gte(RECUR_DAYS_RANGE.min, `The minimum is ${RECUR_DAYS_RANGE.min} day.`)
      .lte(RECUR_DAYS_RANGE.max, `The maximum is ${RECUR_DAYS_RANGE.max} days.`)
      .optional(),

    // End type inputs:
    // not sent to backend
    endType: z.nativeEnum(EndRecurrenceType),
    // nodeData.endDate
    endDate: momentZodType.optional(),
    // nodeData.skipDaysOfWeek
    skipDaysOfWeek: z.array(z.nativeEnum(Day)).max(6, "Can't skip all days.").optional(),
  })
  .refine(
    ({ startDate, endDate, endType }) => {
      const validation =
        endType === EndRecurrenceType.OnDate && endDate ? endDate.isAfter(startDate) : true;

      return validation;
    },
    {
      path: ['endDate'],
      message: 'The end date cannot be before the start date.',
    },
  );

export const SchedulerFormProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const initialNodeData = useSelector(getOpenedNodeData);
  const isStartTimeOptEnabled = useSelector(getIsJourneyStartTimeOptimizationEnabled);

  const defaultValues = useMemo(
    () => getDefaultFormStateValues(initialNodeData, projectTimeZone, isStartTimeOptEnabled),
    [initialNodeData, projectTimeZone, isStartTimeOptEnabled],
  );

  const methods = useForm<SchedulerFormStateValues>({
    defaultValues,
    // validate on both blur and onChange
    mode: 'all',
    resolver: zodResolver(schema),
    shouldUnregister: true,
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
